import { useEffect, useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { Link } from "wouter";
import { searchEmoji } from "./emojiSearch";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";

const EMOJIS: { char: string; keywords: string }[] = [
  // Smileys
  { char: "😀", keywords: "grin happy smile face" },
  { char: "😃", keywords: "happy smile grin joy face" },
  { char: "😄", keywords: "happy smile laugh joy face" },
  { char: "😁", keywords: "grin happy smile teeth face" },
  { char: "😆", keywords: "laugh happy squint face" },
  { char: "😂", keywords: "laugh cry funny lol" },
  { char: "🤣", keywords: "rofl laugh floor funny hilarious" },
  { char: "🙂", keywords: "smile slight happy face" },
  { char: "😊", keywords: "happy blush smile shy face" },
  { char: "😍", keywords: "love heart eyes crush" },
  { char: "🥰", keywords: "love hearts adore" },
  { char: "😘", keywords: "kiss love face blow" },
  { char: "😎", keywords: "cool sunglasses" },
  { char: "🤩", keywords: "star struck excited eyes wow" },
  { char: "🥳", keywords: "party celebrate birthday" },
  { char: "😜", keywords: "wink tongue silly playful" },
  { char: "🤪", keywords: "zany crazy wild silly" },
  { char: "😏", keywords: "smirk sly sneaky" },
  { char: "🤨", keywords: "skeptical raised eyebrow suspicious" },
  { char: "😐", keywords: "neutral blank straight face" },
  { char: "🙄", keywords: "eye roll annoyed unimpressed" },
  { char: "😴", keywords: "sleep tired zzz" },
  { char: "🥱", keywords: "yawn tired bored sleepy" },
  { char: "😢", keywords: "sad cry tear" },
  { char: "😭", keywords: "sob crying sad" },
  { char: "😡", keywords: "angry mad rage" },
  { char: "🤬", keywords: "furious cursing angry swearing" },
  { char: "😱", keywords: "scared shock surprised" },
  { char: "😨", keywords: "fear scared afraid nervous" },
  { char: "🤔", keywords: "thinking hmm" },
  { char: "🙃", keywords: "upside down silly" },
  { char: "😉", keywords: "wink flirt" },
  { char: "🤗", keywords: "hug welcome" },
  { char: "😇", keywords: "angel innocent halo" },
  { char: "🤒", keywords: "sick fever ill thermometer" },
  { char: "🤢", keywords: "nauseous sick gross disgust" },
  { char: "🥵", keywords: "hot sweating overheated" },
  { char: "🥶", keywords: "cold freezing frozen" },
  { char: "😵", keywords: "dizzy confused knocked out" },
  { char: "🤯", keywords: "mind blown shocked exploding head" },
  { char: "🥺", keywords: "pleading puppy eyes hopeful" },
  { char: "😬", keywords: "grimace awkward nervous cringe" },
  // Gestures
  { char: "👍", keywords: "thumbs up like good yes" },
  { char: "👎", keywords: "thumbs down dislike no" },
  { char: "👏", keywords: "clap applause" },
  { char: "🙏", keywords: "pray thanks please" },
  { char: "🤝", keywords: "handshake deal agreement" },
  { char: "✌️", keywords: "peace victory" },
  { char: "👋", keywords: "wave hello bye" },
  { char: "💪", keywords: "strong muscle flex" },
  { char: "🤞", keywords: "fingers crossed luck" },
  { char: "👌", keywords: "ok okay perfect" },
  { char: "🤙", keywords: "call me shaka hang loose" },
  { char: "👆", keywords: "point up finger" },
  { char: "👇", keywords: "point down finger" },
  { char: "👈", keywords: "point left finger" },
  { char: "👉", keywords: "point right finger" },
  { char: "✊", keywords: "fist raised power solidarity" },
  { char: "👊", keywords: "fist bump punch" },
  { char: "🖐️", keywords: "hand stop high five" },
  { char: "🤟", keywords: "love you hand sign rock" },
  { char: "🫡", keywords: "salute respect yes sir" },
  // Hearts
  { char: "❤️", keywords: "heart love red" },
  { char: "💔", keywords: "broken heart sad" },
  { char: "💕", keywords: "hearts love two" },
  { char: "💖", keywords: "sparkling heart love" },
  { char: "💗", keywords: "growing heart love pink" },
  { char: "💓", keywords: "beating heart love pulse" },
  { char: "🧡", keywords: "orange heart love" },
  { char: "💛", keywords: "yellow heart love" },
  { char: "💚", keywords: "green heart love" },
  { char: "💙", keywords: "blue heart love" },
  { char: "💜", keywords: "purple heart love" },
  { char: "🖤", keywords: "black heart love dark" },
  { char: "🤍", keywords: "white heart love pure" },
  { char: "💝", keywords: "heart gift ribbon love" },
  // Animals
  { char: "🐶", keywords: "dog puppy pet" },
  { char: "🐱", keywords: "cat kitten pet" },
  { char: "🦁", keywords: "lion king animal" },
  { char: "🐼", keywords: "panda bear" },
  { char: "🦄", keywords: "unicorn magic" },
  { char: "🐸", keywords: "frog animal" },
  { char: "🐢", keywords: "turtle slow animal" },
  { char: "🦋", keywords: "butterfly insect" },
  { char: "🐘", keywords: "elephant animal big" },
  { char: "🐵", keywords: "monkey animal" },
  { char: "🐰", keywords: "rabbit bunny animal" },
  { char: "🦊", keywords: "fox animal" },
  { char: "🐻", keywords: "bear animal" },
  { char: "🐨", keywords: "koala animal" },
  { char: "🐧", keywords: "penguin animal bird" },
  { char: "🦉", keywords: "owl bird animal wise" },
  { char: "🐴", keywords: "horse animal" },
  { char: "🐝", keywords: "bee insect honey" },
  // Food
  { char: "🍕", keywords: "pizza food" },
  { char: "🍔", keywords: "burger food" },
  { char: "🍩", keywords: "donut sweet food" },
  { char: "☕", keywords: "coffee drink" },
  { char: "🍎", keywords: "apple fruit food" },
  { char: "🍰", keywords: "cake dessert birthday" },
  { char: "🍺", keywords: "beer drink" },
  { char: "🍷", keywords: "wine drink" },
  { char: "🍟", keywords: "fries food snack" },
  { char: "🌮", keywords: "taco food mexican" },
  { char: "🍣", keywords: "sushi food japanese" },
  { char: "🍦", keywords: "ice cream dessert sweet" },
  { char: "🍫", keywords: "chocolate sweet candy" },
  { char: "🍪", keywords: "cookie sweet dessert" },
  { char: "🍜", keywords: "noodles ramen food soup" },
  { char: "🥗", keywords: "salad food healthy" },
  { char: "🍿", keywords: "popcorn snack movie" },
  { char: "🍉", keywords: "watermelon fruit food" },
  // Travel & objects
  { char: "✈️", keywords: "plane travel flight" },
  { char: "🚗", keywords: "car drive travel" },
  { char: "🏖️", keywords: "beach vacation travel" },
  { char: "🎉", keywords: "party celebrate confetti" },
  { char: "🎂", keywords: "birthday cake celebrate" },
  { char: "🎁", keywords: "gift present" },
  { char: "💡", keywords: "idea lightbulb bright" },
  { char: "🔥", keywords: "fire hot lit" },
  { char: "⭐", keywords: "star favorite" },
  { char: "✅", keywords: "check done complete yes" },
  { char: "❌", keywords: "cross wrong no cancel" },
  { char: "⚡", keywords: "lightning bolt fast" },
  { char: "🎯", keywords: "target goal bullseye" },
  { char: "🚀", keywords: "rocket launch fast" },
  { char: "💰", keywords: "money bag cash" },
  { char: "📅", keywords: "calendar date schedule" },
  { char: "⏰", keywords: "alarm clock time" },
  { char: "📱", keywords: "phone mobile device" },
  { char: "💻", keywords: "laptop computer work" },
  { char: "📧", keywords: "email mail message" },
  { char: "🚆", keywords: "train travel transport" },
  { char: "🚢", keywords: "ship boat travel" },
  { char: "🏔️", keywords: "mountain travel nature" },
  { char: "🗽", keywords: "statue liberty travel landmark" },
  { char: "🎒", keywords: "backpack travel bag" },
  { char: "🧳", keywords: "luggage suitcase travel packing" },
  // Weather & nature
  { char: "☀️", keywords: "sun sunny weather bright" },
  { char: "🌧️", keywords: "rain weather cloud" },
  { char: "⛈️", keywords: "storm thunder lightning weather" },
  { char: "❄️", keywords: "snow snowflake cold winter" },
  { char: "🌈", keywords: "rainbow colorful weather" },
  { char: "🌙", keywords: "moon night crescent" },
  { char: "🌊", keywords: "wave ocean water sea" },
  { char: "🌸", keywords: "flower blossom spring pink" },
  { char: "🌻", keywords: "sunflower flower yellow" },
  { char: "🌵", keywords: "cactus desert plant" },
  { char: "🍁", keywords: "leaf autumn fall maple" },
  { char: "🌳", keywords: "tree nature plant green" },
  // Activities & sports
  { char: "⚽", keywords: "soccer football sport ball" },
  { char: "🏀", keywords: "basketball sport ball" },
  { char: "🏈", keywords: "football american sport ball" },
  { char: "🎾", keywords: "tennis sport ball" },
  { char: "🏓", keywords: "ping pong table tennis sport" },
  { char: "🏋️", keywords: "gym workout weightlifting fitness" },
  { char: "🏃", keywords: "run running exercise fitness" },
  { char: "🧘", keywords: "yoga meditate relax mindfulness" },
  { char: "🎮", keywords: "game controller gaming video" },
  { char: "🎸", keywords: "guitar music instrument" },
  { char: "🎨", keywords: "art paint palette creative" },
  { char: "📚", keywords: "books read study education" },
  { char: "🎬", keywords: "movie film clapperboard cinema" },
  { char: "🎵", keywords: "music note song" },
  // Symbols
  { char: "💯", keywords: "hundred perfect score complete" },
  { char: "❗", keywords: "exclamation important alert" },
  { char: "❓", keywords: "question mark confused" },
  { char: "♻️", keywords: "recycle green eco environment" },
  { char: "🔒", keywords: "lock secure private locked" },
  { char: "🔓", keywords: "unlock open unlocked" },
  { char: "⚠️", keywords: "warning caution alert" },
  { char: "🆕", keywords: "new label badge" },
  { char: "🔔", keywords: "bell notification alert ring" },
  { char: "📌", keywords: "pin pushpin location marker" },
  { char: "🔗", keywords: "link chain url connect" },
  { char: "🏆", keywords: "trophy win award champion" },
];

const TOOL_INFO_CONTENT = {
  what: `A searchable emoji picker with the complete Unicode emoji set (about 1,900 emoji), organised by category, with skin-tone choices, so you can find and copy any emoji without scrolling through a phone keyboard.`,
  how: `Search by keyword (like "happy" or "travel") or pick a category, choose a skin tone if you want one, and click any emoji to copy it to your clipboard.`,
  tip: `Try a few different keywords if your first search doesn't find what you're looking for — emoji are tagged by common descriptive words, and different people describe the same emoji slightly differently. How an emoji looks depends on the device you paste it into.`,
};

interface Emo { char: string; label: string; tags: string[]; group: number; skins?: string[] }
const GROUPS = ["Smileys & emotion", "People & body", "Components", "Animals & nature", "Food & drink", "Travel & places", "Activities", "Objects", "Symbols", "Flags"];
const TONES = [["Default", "🖐️"], ["Light", "🖐🏻"], ["Medium-light", "🖐🏼"], ["Medium", "🖐🏽"], ["Medium-dark", "🖐🏾"], ["Dark", "🖐🏿"]];

export default function EmojiPicker() {
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState<number | "all">("all");
  const [tone, setTone] = useState(0);
  const [all, setAll] = useState<Emo[] | null>(null);

  useEffect(() => {
    let live = true;
    import("emojibase-data/en/compact.json").then((m) => {
      if (!live) return;
      const list = (m.default as unknown as { unicode: string; label: string; tags?: string[]; group?: number; skins?: { unicode: string }[] }[])
        .filter((e) => e.group !== undefined)
        .map((e) => ({ char: e.unicode, label: e.label, tags: e.tags ?? [], group: e.group!, skins: e.skins?.filter((k) => k.unicode).map((k) => k.unicode) }));
      setAll(list);
    }).catch(() => setAll(EMOJIS.map((e) => ({ char: e.char, label: e.keywords.split(" ")[0] ?? "", tags: e.keywords.split(" "), group: 0 }))));
    return () => { live = false; };
  }, []);

  const filtered = useMemo(() => {
    const list = all ?? EMOJIS.map((e) => ({ char: e.char, label: "", tags: e.keywords.split(" "), group: 0 } as Emo));
    const q = search.trim().toLowerCase();
    void q;
    return searchEmoji(list.filter((e) => group === "all" || e.group === group), search);
  }, [all, search, group]);

  const { logUsageNow } = useLogUsageOnAction();
  const shown = (e: Emo) => (tone > 0 && e.skins && e.skins.length >= 5 ? e.skins[tone - 1] : e.char);

  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (char: string) => {
    const allowed = await logUsageNow("copy");
    if (!allowed) return;
    try {
      await navigator.clipboard.writeText(char);
      setCopied(char);
      setTimeout(() => setCopied((c) => (c === char ? null : c)), 2500);
      toast.success(`Copied ${char}`);
    } catch {
      toast.error("Couldn't copy automatically. Select the emoji and copy it by hand.");
    }
  };

  return (
    <ToolContainer>
      <Input
        placeholder="Search emoji by keyword (e.g. happy, flag india, cat)..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="mb-3"
      />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button type="button" onClick={() => setGroup("all")} className={`text-xs rounded-full border px-3 py-1 ${group === "all" ? "bg-primary text-primary-foreground" : ""}`}>All</button>
        {GROUPS.map((g, i) => (i === 2 ? null : <button key={g} type="button" onClick={() => setGroup(i)} className={`text-xs rounded-full border px-3 py-1 ${group === i ? "bg-primary text-primary-foreground" : ""}`}>{g}</button>))}
        <select aria-label="Skin tone" value={tone} onChange={(e) => setTone(Number(e.target.value))} className="ml-auto h-8 rounded-md border bg-background px-2 text-xs">
          {TONES.map(([n, ico], i) => <option key={n} value={i}>{ico} {n} skin tone</option>)}
        </select>
      </div>
      <p className="text-sm font-medium mb-1 min-h-5" role="status" aria-live="polite" data-testid="ep-copied">{copied ? `Copied ${copied} to your clipboard` : ""}</p>
      <p className="text-xs text-muted-foreground mb-2">{all ? `${filtered.length.toLocaleString()} emoji` : "Loading the full emoji set…"}</p>
      <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-2">
        {filtered.map(e => (
          <button
            key={e.char}
            type="button"
            onClick={() => copy(shown(e))}
            className="text-3xl p-2 rounded-lg hover:bg-muted transition-colors"
            title={e.label || e.tags.join(" ")}
            aria-label={`Copy ${e.label || e.tags[0] || "emoji"}`}
          >
            {shown(e)}
          </button>
        ))}
      </div>
      {filtered.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">
          No emoji found for "{search}"
        </p>
      )}
      <p className="text-xs text-muted-foreground mt-4" data-testid="ep-crosslink">Need the code for an emoji (U+ code point, HTML entity, CSS or JavaScript escape)? Use the <Link href="/tool/emoji-unicode-converter" className="underline">Emoji to Unicode Converter</Link>.</p>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
